import {
	IUserProfileService,
	OnboardingState,
	OnboardingStep,
	PosturinnSettingsKey,
	UserProfile,
} from '../@posturinn/client-react';
import {
	ConnectionStateService,
	IAccountService,
	IAuthService,
	IMobizStateService,
	RegisterViewModel,
	ResetPasswordViewModel,
	SignUpViewModel,
	VerificationId,
} from '../@posturinn/common-react/core';
import { take, tap, mergeMap, switchMap, map, catchError, takeWhile, filter, delay } from 'rxjs/operators';
import {
	IMobizSettingsService,
	MobizSettings,
	MobizSettingsKey,
	MobizSettingsScope,
} from '../@posturinn/common-react/settings';
import { INITILIZE_INJECTOR } from '../constants/actions/mobizActions';
import {
	FORGOT_PASSWORD_ERROR,
	FORGOT_PASSWORD_PENDING,
	FORGOT_PASSWORD_SUCCESS,
	GET_USER_PROFILE_ERROR,
	GET_USER_PROFILE_FULFILLED,
	GET_USER_PROFILE_PENDING,
	LOGIN_FAILED,
	LOGIN_LOADING,
	LOGOUT_FAILED,
	LOGOUT_FULFILLED,
	LOGOUT_LOADING,
	RESET_PASSWORD_ERROR,
	RESET_PASSWORD_PENDING,
	RESET_PASSWORD_SUCCESS,
	SHOW_LOGIN,
	SIGN_UP_ERROR,
	SIGN_UP_PENDING,
	SIGN_UP_SUCCESS,
	UPDATE_USER_ADDRESS_ERROR,
	UPDATE_USER_ADDRESS_FULFILLED,
	UPDATE_USER_ADDRESS_PENDING,
	UPDATE_USER_PROFILE_ERROR,
	UPDATE_USER_PROFILE_FULFILLED,
	UPDATE_USER_PROFILE_PENDING,
	SET_AUDKENNI_VERIFICATION_CODE,
} from '../constants/actions/userProfileActions';
import { closeAllModals } from './modalActions';
import { AudkenniCode } from '../@posturinn/common-react/core/data/audkenni-code.do';
import { TextResponse } from '../@posturinn/common-react/core/data/text-response.do';
import { interval, Observable, of } from 'rxjs';

export const getUserProfile = () => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const userProfileService: IUserProfileService = injector.get(IUserProfileService);
		dispatch(getUserProfilePending());
		userProfileService.getUserProfile().subscribe({
			next: (userProfile: UserProfile) => {
				dispatch(getUserProfileFulfilled(userProfile));
			},
			error: () => {
				dispatch(getUserProfileError());
			},
		});
	};
};

export const updateUserProfile = (newUserProfile: UserProfile) => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const userProfileService: IUserProfileService = injector.get(IUserProfileService);
		if (newUserProfile.phoneNumber && newUserProfile.phoneNumber.length === 7)
			newUserProfile.phoneNumber = '+354' + newUserProfile.phoneNumber;
		dispatch(updateUserProfilePending());
		userProfileService.updateUserProfile(newUserProfile).subscribe({
			next: (userProfile: UserProfile) => {
				dispatch(updateUserProfileFulfilled(userProfile));
				dispatch(closeAllModals());
				document.body.style.overflow = 'unset';
			},
			error: error => {
				let err = '';
				try {
					err = error.info.details;
				} catch (e) {}
				dispatch(updateUserProfileError(err));
			},
		});
	};
};

export const getUserProfileFulfilled = data => ({
	type: GET_USER_PROFILE_FULFILLED,
	payload: {
		userProfile: data,
	},
});

export const getUserProfilePending = () => ({
	type: GET_USER_PROFILE_PENDING,
	payload: null,
});

export const getUserProfileError = () => ({
	type: GET_USER_PROFILE_ERROR,
	payload: null,
});

export const updateUserProfileFulfilled = data => ({
	type: UPDATE_USER_PROFILE_FULFILLED,
	payload: {
		userProfile: data,
	},
});

export const updateUserProfilePending = () => ({
	type: UPDATE_USER_PROFILE_PENDING,
});

export const updateUserProfileError = error => ({
	type: UPDATE_USER_PROFILE_ERROR,
	payload: error,
});

export const updateUserAddressFulfilled = data => ({
	type: UPDATE_USER_ADDRESS_FULFILLED,
	payload: {
		userProfile: data,
	},
});

export const updateUserAddressPending = () => ({
	type: UPDATE_USER_ADDRESS_PENDING,
});

export const updateUserAddressError = error => ({
	type: UPDATE_USER_ADDRESS_ERROR,
	payload: error,
});

export const setAudkenniVerificationCode = (verificationCode: string) => ({
	type: SET_AUDKENNI_VERIFICATION_CODE,
	payload: verificationCode,
});

export const showLogin = () => ({
	type: SHOW_LOGIN,
});

export const setInjector = injector => ({
	type: INITILIZE_INJECTOR,
	payload: {
		injector,
	},
});

export const initilizeInjector = injector => {
	return async function (dispatch) {
		try {
			dispatch(getUserProfilePending());
			dispatch(setInjector(injector));
			const authService: IAuthService = injector.get(IAuthService);
			const connectionStateService: ConnectionStateService = injector.get(ConnectionStateService);
			if (authService.isLoggedIn()) {
				const stateService: IMobizStateService = injector.get(IMobizStateService);
				connectionStateService.init().subscribe({
					next: () => {
						stateService.init().subscribe({
							next: () => {
								if (!stateService.currentUser.value) {
									throw new Error('Please log in.');
								}
								const membership = stateService.currentUser.value.membership.find(
									membership =>
										// @ts-ignore
										membership.organization.identificationNumber ===
										stateService.currentUser.value.identificationNumber
								);

								if (!membership) {
									throw new Error('Missing membership data.');
								}
								// Posturinn accounts have their own membership organization.
								stateService.changeCurrentMembership(membership);
								dispatch(getUserProfile());
							},
							error: () => {
								// Log user out
								console.log('State service init error, showing login');
								dispatch(showLogin());
								authService
									.logOut()
									.toPromise()
									.then()
									.catch(err => {
										console.log(err);
									});
							},
						});
					},
					error: err => {
						console.log('Error initializing connection state', err);
					},
				});
			} else {
				connectionStateService.init();
				dispatch(showLogin());
			}
		} catch (err) {
			console.error(err);
		}
	};
};

const pollUntilSuccessHelper = (authService: IAuthService, verificationId: string): Observable<string> => {
	let attempts = 0;
	// Auðkenni gives the user 120 seconds to confirm the auth so this needs to be minimum 120 seconds
	//  which means maxAttempts * pollingRate needs to be above 120
	const maxAttempts = 45;
	const pollingRate = 3;

	// pollingRate*1000 is converting the polling rate from seconds to milliseconds as interval() expects milliseconds
	return interval(pollingRate * 1000).pipe(
		switchMap(() => {
			attempts++;
			if (attempts > maxAttempts) {
				throw new Error('page-login.audkenni.timeout');
			}
			// attempts===maxAttempts is true for the last pollAuthStatus call,
			//   and it removes the timeout as a hail mary in case the users internet connection is terrible
			return authService.pollAuthStatus(verificationId, attempts === maxAttempts).pipe(
				map((response: TextResponse) => response.text),
				catchError(error => {
					throw new Error(error.info.details);
				})
			);
		}),
		takeWhile(status => status === 'In progress', true),
		filter(status => status === 'Success'),
		take(1)
	);
};

export const audkenniLogin = (phoneOrSsn: string, useAudkenniApp: boolean) => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const authService: IAuthService = injector.get(IAuthService);
		dispatch({ type: LOGIN_LOADING });

		try {
			authService
				.getAudkenniCode()
				.pipe(
					take(1),
					tap((audkenniCode: AudkenniCode) => {
						dispatch(setAudkenniVerificationCode(audkenniCode.verificationCode));
					}),
					delay(3000), // arbitrary 3-second delay to allow some time before we start polling
					//   and to give the user time to remember the verification code before the phone blocks the view by prompting auth on the phone
					mergeMap((audkenniCode: AudkenniCode) => {
						return authService.initializeAuthPoll(phoneOrSsn, audkenniCode.hash, useAudkenniApp);
					}),
					mergeMap((verificationId: TextResponse) => {
						if (!verificationId.text) {
							throw new Error('page-login.loginFailed');
						}
						return pollUntilSuccessHelper(authService, verificationId.text).pipe(
							mergeMap(() => dispatch(checkLoginCredentials(verificationId.text, 'DUMMY_PASSWORD'))),
							catchError((error: Error) => {
								dispatch({ type: LOGIN_FAILED, payload: error.message });
								return of(null);
							})
						);
					}),
					catchError(() => {
						dispatch({ type: LOGIN_FAILED, payload: 'loginFailed' });
						return of(null);
					})
				)
				.subscribe({
					next: (): void => {},
				});
		} catch (error) {
			let errorMessage = 'loginFailed';
			if (error.message) {
				errorMessage = error.message;
			}
			dispatch({ type: LOGIN_FAILED, payload: errorMessage });
		}
	};
};

export const checkLoginCredentials = (username: string, password: string) => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const authService: IAuthService = injector.get(IAuthService);
		const stateService: IMobizStateService = injector.get(IMobizStateService);
		window.sessionStorage.removeItem('onboardingTab');
		window.sessionStorage.removeItem('onboardingEmail');
		window.sessionStorage.removeItem('onboardingPhone');

		dispatch({ type: LOGIN_LOADING });
		authService.logIn({ username, password }).subscribe({
			next: () => {
				stateService.init().subscribe({
					next: () => {
						if (!stateService.currentUser.value) {
							throw new Error('Please log in.');
						}
						const membership = stateService.currentUser.value.membership.find(
							membership =>
								// @ts-ignore
								membership.organization.identificationNumber ===
								stateService.currentUser.value.identificationNumber
						);
						if (!membership) {
							//throw new Error('Missing membership data.');

							dispatch({ type: LOGIN_FAILED, payload: 'user_fyrtaekjasidur' });
							authService
								.logOut()
								.toPromise()
								.then()
								.catch(err => {
									console.log(err);
								});
							return false;
						}
						// Posturinn accounts have their own membership organization.
						stateService.changeCurrentMembership(membership);
						window.scrollTo(0, 0);
						dispatch(getUserProfile());
						dispatch(setAudkenniVerificationCode(null));
					},
					error: () => {
						console.log('ERROR INITILIZING STATE SERVICE');
					},
				});
			},
			error: error => {
				dispatch({ type: LOGIN_FAILED, payload: error.originalError.info.extras.error });
			},
		});
	};
};

export const logout = () => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const authService: IAuthService = injector.get(IAuthService);
		dispatch({ type: LOGOUT_LOADING });
		authService.logOut().subscribe({
			next: () => {
				dispatch({ type: LOGOUT_FULFILLED });
			},
			error: error => {
				dispatch({ type: LOGOUT_FAILED, payload: error });
			},
		});
	};
};

export const forgotPassword = (email: string) => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const authService: IAuthService = injector.get(IAuthService);
		dispatch({ type: FORGOT_PASSWORD_PENDING });
		authService.forgotPassword(email).subscribe({
			next: () => {
				dispatch({ type: FORGOT_PASSWORD_SUCCESS });
			},
			error: error => {
				dispatch({ type: FORGOT_PASSWORD_ERROR, payload: error.message });
			},
		});
	};
};

export const resetPassword = (resetPasswordViewModel: ResetPasswordViewModel) => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const accountService: IAccountService = injector.get(IAccountService);
		dispatch({ type: RESET_PASSWORD_PENDING });
		accountService.resetPassword(resetPasswordViewModel).subscribe({
			next: (registerViewModel: RegisterViewModel) => {
				dispatch({ type: RESET_PASSWORD_SUCCESS, payload: registerViewModel });
			},
			error: error => {
				let errorMessage = '';
				if (error && error.info && error.info.extras) {
					for (const err in error.info.extras) {
						if (error.info.extras?.hasOwnProperty(err)) {
							errorMessage += error.info.extras[err] + ' ';
						}
					}
				}
				dispatch({ type: RESET_PASSWORD_ERROR, payload: errorMessage ?? error.message });
			},
		});
	};
};

export const signUp = (signUpViewModel: SignUpViewModel) => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const accountService: IAccountService = injector.get(IAccountService);
		dispatch({ type: SIGN_UP_PENDING });
		accountService.signUp(signUpViewModel).subscribe({
			next: (verificationId: VerificationId) => {
				dispatch({ type: SIGN_UP_SUCCESS, payload: verificationId });
			},
			error: error => {
				dispatch({
					type: SIGN_UP_ERROR,
					payload: JSON.stringify(error.status) === '500' ? '' : JSON.stringify(error.info.details),
				});
			},
		});
	};
};

export const agreeToGdpr = newUserProfile => {
	return function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const userProfileService: IUserProfileService = injector.get(IUserProfileService);
		dispatch({ type: UPDATE_USER_PROFILE_PENDING });
		userProfileService.acceptGdprTerms().subscribe({
			next: () => {
				userProfileService.updateUserProfile(newUserProfile).subscribe({
					next: (userProfile: UserProfile) => {
						dispatch(markOnboardingDone(userProfile.userId));
						dispatch(updateUserProfileFulfilled(userProfile));
						document.body.style.overflow = 'unset';
					},
					error: error => {
						dispatch({ type: UPDATE_USER_PROFILE_ERROR, payload: error });
					},
				});
			},
			error: error => {
				dispatch({ type: UPDATE_USER_PROFILE_ERROR, payload: error });
			},
		});
	};
};

const settingsScope: MobizSettingsScope = 'user';
const settingsKey: PosturinnSettingsKey = 'PosturinnUserOnboarding';

const markOnboardingDone = (userId: string) => {
	return async function (dispatch, getState) {
		const { injector } = getState().mobizReducer;
		const settingsService: IMobizSettingsService = injector.get(IMobizSettingsService);

		settingsService
			.get<OnboardingState>(
				settingsScope,
				userId,
				settingsKey as MobizSettingsKey,
				new MobizSettings<OnboardingState>(OnboardingState)
			)
			.subscribe({
				next: (settings: MobizSettings<OnboardingState>) => {
					dispatch(updateOnboardingSettings(settings, userId, injector));
				},
				error: error => {
					dispatch({ type: UPDATE_USER_PROFILE_ERROR, payload: error });
				},
			});
	};
};

const updateOnboardingSettings = (settings: MobizSettings<OnboardingState>, userId: string, injector) => {
	return function () {
		const settingsService: IMobizSettingsService = injector.get(IMobizSettingsService);

		settings.value.currentStep = OnboardingStep.DONE;

		settings.origin === 'DB'
			? settingsService.update(settingsScope, userId, settingsKey, settings).subscribe({
					next: () => {},
			  })
			: settingsService.add(settingsScope, userId, settingsKey, settings).subscribe({
					next: () => {},
			  });
	};
};
